import { FixtureAssignment } from '../allocation/computeFixtureAllocations';
import { SubcategoryAllocation } from '../types/zoneMapType';

/**
 * Post-processes an SVG string from FloorplanViewer's exportToSvg() to:
 * 1. Inject linear gradients for boundary fixtures
 * 2. Append a legend table to the right of the floorplan
 *
 * Uses DOM parsing for reliable SVG manipulation.
 */
export function injectSvgGradients(
  svgString: string,
  fixtureAssignments: FixtureAssignment[],
  allocations?: SubcategoryAllocation[],
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');
  const svg = doc.documentElement;

  // Bail out if parsing failed
  const parseError = doc.querySelector('parsererror');
  if (parseError) return svgString;

  // --- Gradient injection for boundary fixtures ---
  injectBoundaryGradients(doc, svg, fixtureAssignments);

  // --- Legend table ---
  if (allocations && allocations.length > 0) {
    appendLegendTable(doc, svg, allocations, fixtureAssignments);
  }

  return new XMLSerializer().serializeToString(doc);
}

/**
 * Injects SVG <linearGradient> definitions for boundary fixtures and
 * replaces their solid fill with the gradient reference.
 */
function injectBoundaryGradients(doc: Document, svg: Element, fixtureAssignments: FixtureAssignment[]): void {
  const boundaryFixtures = fixtureAssignments.filter(
    (fa) => fa.isBoundary && fa.boundaryGradient && fa.boundaryGradient.length >= 2,
  );
  if (boundaryFixtures.length === 0) return;

  // Ensure <defs> exists
  let defs = svg.querySelector('defs');
  if (!defs) {
    defs = doc.createElementNS('http://www.w3.org/2000/svg', 'defs');
    svg.insertBefore(defs, svg.firstChild);
  }

  // Build lookup: for each boundary fixture, compute what drawSvg would have written
  // as the displayText (from the display mode render). We match <g> elements by finding
  // the <text> content AND the solid fillColor on the <rect>.
  const boundaryLookup: Array<{
    fa: FixtureAssignment;
    gradientId: string;
    expectedFill: string;
    expectedText: string;
  }> = [];

  for (const fa of boundaryFixtures) {
    const gradientId = `grad-${fa.fixtureId.replace(/[^a-zA-Z0-9]/g, '_')}`;
    // The display mode renders boundary fixtures with text like "Ap/Tr"
    const expectedText = (fa.boundaryNames ?? []).map((n) => n.slice(0, 2)).join('/');
    // And fillColor is the primary subcategory color
    const expectedFill = fa.color;
    boundaryLookup.push({ fa, gradientId, expectedFill, expectedText });

    // Create the gradient definition
    const gradient = doc.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
    gradient.setAttribute('id', gradientId);
    gradient.setAttribute('gradientUnits', 'objectBoundingBox');
    gradient.setAttribute('x1', '0');
    gradient.setAttribute('y1', '0');
    gradient.setAttribute('x2', '1');
    gradient.setAttribute('y2', '0');

    const stops = fa.boundaryGradient!;
    const totalWeight = stops.reduce((sum, s) => sum + s.weight, 0);
    let cumulative = 0;
    for (const stop of stops) {
      const startPct = (cumulative / totalWeight) * 100;
      cumulative += stop.weight;
      const endPct = (cumulative / totalWeight) * 100;
      appendStop(doc, gradient, `${startPct.toFixed(1)}%`, stop.color);
      appendStop(doc, gradient, `${endPct.toFixed(1)}%`, stop.color);
    }
    defs.appendChild(gradient);
  }

  // Single pass: for each <g>, check if its text+fill matches a boundary fixture
  const gElements = svg.querySelectorAll('g[transform]');
  for (const g of gElements) {
    const rect = g.querySelector('rect');
    const text = g.querySelector('text');
    if (!rect || !text) continue;

    const fill = rect.getAttribute('fill') ?? '';
    const textContent = text.textContent ?? '';

    // Find matching boundary fixture by text content and fill color
    const idx = boundaryLookup.findIndex((entry) => entry.expectedText === textContent && entry.expectedFill === fill);
    if (idx === -1) continue;

    const entry = boundaryLookup[idx];
    rect.setAttribute('fill', `url(#${entry.gradientId})`);
    // Remove so we don't match the same entry twice
    boundaryLookup.splice(idx, 1);
  }
}

function appendStop(doc: Document, gradient: Element, offset: string, color: string): void {
  const stop = doc.createElementNS('http://www.w3.org/2000/svg', 'stop');
  stop.setAttribute('offset', offset);
  stop.setAttribute('stop-color', color);
  gradient.appendChild(stop);
}

/**
 * Appends a legend table to the right of the floorplan.
 * Columns: Abbr | Subcategory (colored pill) | % | Fixtures
 */
function appendLegendTable(
  doc: Document,
  svg: Element,
  allocations: SubcategoryAllocation[],
  fixtureAssignments: FixtureAssignment[],
): void {
  const viewBox = svg.getAttribute('viewBox');
  if (!viewBox) return;

  const parts = viewBox.split(/\s+/).map(Number);
  if (parts.length < 4 || parts.some(isNaN)) return;
  const [vbX, vbY, vbW, vbH] = parts;

  // Count fixtures per subcategory
  const fixtureCountMap = new Map<string, number>();
  for (const fa of fixtureAssignments) {
    fixtureCountMap.set(fa.subcategoryId, (fixtureCountMap.get(fa.subcategoryId) || 0) + 1);
  }

  // Find the longest subcategory name to size the pill column
  const maxNameLen = Math.max(...allocations.map((a) => a.subcategoryName.length), 8);

  // Table sizing — compact relative to floorplan height
  const fontSize = vbH * 0.012;
  const rowHeight = fontSize * 2.2;
  const padding = fontSize * 1.5;
  const colAbbr = fontSize * 3;
  const colSubcat = fontSize * 0.55 * maxNameLen + fontSize * 2; // sized to longest name
  const colPct = fontSize * 3.5;
  const colFixtures = fontSize * 4;
  const tableWidth = colAbbr + colSubcat + colPct + colFixtures + padding * 2;
  const tableHeight = padding + rowHeight * (allocations.length + 1) + padding;

  // Position to the right with a gap
  const gap = vbW * 0.05;
  const tableX = vbX + vbW + gap;

  // Expand viewBox to fit — also expand height if legend is taller than floorplan
  const newVbH = Math.max(vbH, tableHeight);
  const newVbY = vbY + (vbH - newVbH) / 2; // keep floorplan vertically centered
  const tableY = newVbY + (newVbH - tableHeight) / 2;

  svg.setAttribute('viewBox', `${vbX} ${newVbY} ${vbW + gap + tableWidth + padding} ${newVbH}`);

  const g = doc.createElementNS('http://www.w3.org/2000/svg', 'g');
  g.setAttribute('class', 'legend-table');

  // Background
  g.appendChild(
    createRect(doc, tableX, tableY, tableWidth, tableHeight, '#ffffff', '#e0e0e0', fontSize * 0.04, fontSize * 0.3),
  );

  // Header
  const headerY = tableY + padding + fontSize;
  const colStarts = {
    abbr: tableX + padding,
    subcat: tableX + padding + colAbbr,
    pct: tableX + padding + colAbbr + colSubcat,
    fixtures: tableX + padding + colAbbr + colSubcat + colPct,
  };

  g.appendChild(createText(doc, colStarts.abbr, headerY, 'Abbr', fontSize, '#333', 'bold'));
  g.appendChild(createText(doc, colStarts.subcat, headerY, 'Subcategory', fontSize, '#333', 'bold'));
  g.appendChild(createText(doc, colStarts.pct, headerY, '%', fontSize, '#333', 'bold'));
  g.appendChild(createText(doc, colStarts.fixtures, headerY, 'Fixtures', fontSize, '#333', 'bold'));

  // Header separator
  const sepY = headerY + fontSize * 0.6;
  g.appendChild(
    createLine(doc, tableX + padding * 0.5, sepY, tableX + tableWidth - padding * 0.5, sepY, '#ddd', fontSize * 0.04),
  );

  // Data rows
  allocations.forEach((alloc, i) => {
    const rowY = sepY + (i + 1) * rowHeight - rowHeight * 0.3;
    const count = fixtureCountMap.get(alloc.subcategoryId) ?? 0;
    const abbr = alloc.subcategoryName.slice(0, 2);

    // Abbr
    g.appendChild(createText(doc, colStarts.abbr, rowY, abbr, fontSize, '#333'));

    // Colored pill with subcategory name
    const pillPadH = fontSize * 0.4;
    const pillW = fontSize * 0.55 * alloc.subcategoryName.length + pillPadH * 2;
    const pillH = fontSize * 1.3;
    const pillY = rowY - fontSize * 0.75;
    g.appendChild(createRect(doc, colStarts.subcat, pillY, pillW, pillH, alloc.color, 'none', 0, fontSize * 0.25));
    const pillTextColor = isLightColor(alloc.color) ? '#333' : '#fff';
    g.appendChild(
      createText(doc, colStarts.subcat + pillPadH, rowY, alloc.subcategoryName, fontSize * 0.8, pillTextColor, 'bold'),
    );

    // Percentage
    g.appendChild(createText(doc, colStarts.pct, rowY, `${alloc.percentage}%`, fontSize, '#333'));

    // Fixture count
    g.appendChild(createText(doc, colStarts.fixtures, rowY, String(count), fontSize, '#333'));

    // Row separator (light)
    if (i < allocations.length - 1) {
      const rSepY = rowY + rowHeight * 0.5;
      g.appendChild(
        createLine(
          doc,
          tableX + padding * 0.5,
          rSepY,
          tableX + tableWidth - padding * 0.5,
          rSepY,
          '#f0f0f0',
          fontSize * 0.02,
        ),
      );
    }
  });

  svg.appendChild(g);
}

function createText(
  doc: Document,
  x: number,
  y: number,
  text: string,
  fontSize: number,
  fill: string,
  fontWeight = 'normal',
): SVGTextElement {
  const el = doc.createElementNS('http://www.w3.org/2000/svg', 'text') as SVGTextElement;
  el.setAttribute('x', String(x));
  el.setAttribute('y', String(y));
  el.setAttribute('font-family', 'Arial, Helvetica, sans-serif');
  el.setAttribute('font-size', String(fontSize));
  el.setAttribute('font-weight', fontWeight);
  el.setAttribute('fill', fill);
  el.textContent = text;
  return el;
}

function createLine(
  doc: Document,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  stroke: string,
  strokeWidth: number,
): SVGLineElement {
  const el = doc.createElementNS('http://www.w3.org/2000/svg', 'line') as SVGLineElement;
  el.setAttribute('x1', String(x1));
  el.setAttribute('y1', String(y1));
  el.setAttribute('x2', String(x2));
  el.setAttribute('y2', String(y2));
  el.setAttribute('stroke', stroke);
  el.setAttribute('stroke-width', String(strokeWidth));
  return el;
}

function createRect(
  doc: Document,
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
  stroke: string,
  strokeWidth: number,
  rx: number,
): SVGRectElement {
  const el = doc.createElementNS('http://www.w3.org/2000/svg', 'rect') as SVGRectElement;
  el.setAttribute('x', String(x));
  el.setAttribute('y', String(y));
  el.setAttribute('width', String(w));
  el.setAttribute('height', String(h));
  el.setAttribute('fill', fill);
  if (stroke !== 'none') {
    el.setAttribute('stroke', stroke);
    el.setAttribute('stroke-width', String(strokeWidth));
  }
  el.setAttribute('rx', String(rx));
  return el;
}

/**
 * Determines if a hex color is "light" (high luminance) for text contrast decisions.
 * Uses relative luminance formula: dark text on light backgrounds, white text on dark.
 */
function isLightColor(hex: string): boolean {
  const color = hex.replace('#', '');
  if (color.length < 6) return false;
  const r = parseInt(color.substring(0, 2), 16) / 255;
  const g = parseInt(color.substring(2, 4), 16) / 255;
  const b = parseInt(color.substring(4, 6), 16) / 255;
  // Relative luminance (ITU-R BT.709)
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.5;
}
